import { FilterInputTypes, FilterInputValue, FilterValue } from './types';
import {
  Operator,
  OPERATOR_DESCRIPTION,
  OPERATORS_TYPE,
  SENSITIVE_VALUES,
} from './constants';
import { Column } from '@datum-group/isogd-openapi/lib';
import { getColumnFilterType } from '..';
import { DefaultOptionType } from '@rc-component/select/lib/Select';

export const createFilterInputValue = (
  { value, operator }: FilterInputValue,
  type: FilterInputTypes,
) => {
  if (type === 'boolean' && Array.isArray(value)) {
    if (value[0] === true && value[1] === true) {
      return {
        value: 'false',
        operator: 'isnull',
      };
    } else if (value[0] === false && value[1] === false) {
      return {
        value: 'true',
        operator: 'isnull',
      };
    } else if (value[0] === true && value[1] === false) {
      return {
        value: 'true',
        operator: 'exact',
      };
    } else if (value[0] === false && value[1] === true) {
      return {
        value: 'false',
        operator: 'exact',
      };
    }
  }

  if (type === 'other' && typeof value === 'boolean') {
    return {
      value: '' + value,
      operator: 'isnull',
    };
  }

  if (
    typeof operator === 'string' &&
    (typeof value === 'string' || typeof value === 'number')
  ) {
    return {
      value: '' + value,
      operator: operator,
    };
  }

  return;
};

export const serializeFilterInputValue = (
  filterValue: FilterInputValue,
  type: FilterInputTypes,
) => {
  const resFilterValue = createFilterInputValue(filterValue, type);

  if (resFilterValue === undefined) return;

  const { operator, value } = resFilterValue;

  return { operator, value };
};

export const serializeFilters = (
  columns: Column[],
  filterValues: FilterValue,
): Record<string, string> => {
  let result: Record<string, string> = {};

  columns.forEach((column) => {
    const filterValue = filterValues[column.column_name];

    if (filterValue === undefined) return;

    const serializedFilterValue = serializeFilterInputValue(
      filterValue,
      getColumnFilterType(column),
    );

    if (!serializedFilterValue) return;

    const { operator, value } = serializedFilterValue;

    if (value === '') return;

    const key = `${column.column_name}${operator ? '__' + operator : ''}`;

    result[key] = value;
  });

  return result;
};

export function isBooleanTuple(value: any): value is [boolean, boolean] {
  return (
    Array.isArray(value) &&
    typeof value[0] === 'boolean' &&
    typeof value[1] === 'boolean'
  );
}

export function getOperatorSelectOptionsForInputType(type: FilterInputTypes) {
  return OPERATORS_TYPE[type].initial.map((value) => ({
    value,
    label: OPERATOR_DESCRIPTION[value],
  }));
}

export const OPERATOR_SELECT_OPTIONS: Record<
  FilterInputTypes,
  DefaultOptionType[]
> = {
  string: getOperatorSelectOptionsForInputType('string'),
  number: getOperatorSelectOptionsForInputType('number'),
  date: getOperatorSelectOptionsForInputType('date'),
  time: getOperatorSelectOptionsForInputType('time'),
  datetime: getOperatorSelectOptionsForInputType('datetime'),
  boolean: getOperatorSelectOptionsForInputType('boolean'),
  other: getOperatorSelectOptionsForInputType('other'),
  'table-record-reference': getOperatorSelectOptionsForInputType(
    'table-record-reference',
  ),
};

export const isSensitiveValue = (value: string): value is SENSITIVE_VALUES => {
  return Object.keys(SENSITIVE_VALUES).includes(value);
};

const rangeOperators: Operator[] = [
  SENSITIVE_VALUES.range,
  SENSITIVE_VALUES['range!'],
];

export function isSwitchRangeOperator(
  value: FilterInputValue,
  operator: Operator | undefined,
  filterType: FilterInputTypes,
) {
  const stringOperator = operator;
  const stringValue = value.operator;
  const initialDateTypes = OPERATORS_TYPE[filterType].initial;
  const oppositeDateTypes = OPERATORS_TYPE[filterType].opposite;

  const nonRangeFilters = [...initialDateTypes, ...oppositeDateTypes].filter(
    (operator) => !rangeOperators.includes(operator),
  );

  if (
    stringValue &&
    stringOperator &&
    rangeOperators.includes(stringValue) &&
    nonRangeFilters.includes(stringOperator)
  ) {
    return true;
  }

  if (
    stringValue &&
    stringOperator &&
    rangeOperators.includes(stringOperator) &&
    nonRangeFilters.includes(stringValue)
  ) {
    return true;
  }

  if (
    stringOperator === undefined &&
    stringValue &&
    rangeOperators.includes(stringValue)
  ) {
    return true;
  }
  if (
    stringValue === undefined &&
    stringOperator &&
    rangeOperators.includes(stringOperator)
  ) {
    return true;
  }

  return false;
}
